import { useForm } from 'react-hook-form';

import type { GridApi } from '@mint-lib/ag-grid/community';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { Button, Input, InputHelperText } from '@mint-lib/tw-components';
import { MediaPlan } from '@mint-sdk/api-media-plan';

const MAX_MEDIA_ROWS = 100;
const MIN_MEDIA_ROWS = 1;

type AddMediaRowsProps = {
  mediaData: MediaPlan.MediaData;
  onClose: () => void;
  api: GridApi<MediaPlan.MediaData>;
};

type FormData = {
  countInput: number;
};

const AddMediaRows = ({ mediaData, onClose, api }: AddMediaRowsProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      countInput: MIN_MEDIA_ROWS,
    },
  });

  const { mutateAsync: addMediaRows } = MediaPlan.useAddMediaRows();

  const onSubmit = async (data: FormData) => {
    onClose();

    const context = api.getGridOption('context');

    api.setGridOption('context', {
      ...context,
      rowIdsToExpand: [...context.rowIdsToExpand, mediaData.id],
    });

    try {
      await addMediaRows({
        urlParams: { mediaItemId: mediaData.mediaItemId },
        data: { mediaRowsCount: data.countInput },
      });
    } catch {
      enqueueSnackbar({
        title: t('Failed to add media rows'),
        variant: 'error',
        removable: true,
      });
    }
  };

  return (
    <form className="w-74 px-3 py-1" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-3 flex items-center justify-between">
        <h6 className="mb-0 text-sm text-text-secondary">
          {t('Add media rows inside')}
        </h6>
        <Button
          className="w-[43px]"
          color="secondary"
          variant="contained"
          size="small"
          type="submit"
          aria-label="Add rows submit"
          disabled={!isValid}
        >
          {t('Add')}
        </Button>
      </div>
      <label className="text-xs text-text-secondary">
        <span>{t('Media rows amount')}</span>
        <Input
          className="w-full"
          size="small"
          type="number"
          {...register('countInput', {
            required: t('This field is required.'),
            min: {
              value: MIN_MEDIA_ROWS,
              message: t('Can not add less than {{min}} row', {
                min: MIN_MEDIA_ROWS,
              }),
            },
            max: {
              value: MAX_MEDIA_ROWS,
              message: t('Can not add more than {{max}} rows at once', {
                max: MAX_MEDIA_ROWS,
              }),
            },
            valueAsNumber: true,
          })}
        />
        {errors.countInput && (
          <InputHelperText
            helperText={errors.countInput.message || ''}
            color="error"
          />
        )}
      </label>
    </form>
  );
};

export default AddMediaRows;
